import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mic, Shield, CheckCircle } from 'lucide-react';

const StepsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  position: relative;
  
  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.xs};
    transform: scale(0.9);
  }
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StepCircle = styled(motion.div)`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isActive, isCompleted, theme }) => {
    if (isCompleted) return theme.colors.status.online;
    if (isActive) return `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent.purple})`;
    return theme.colors.dark.elevated;
  }};
  border: 2px solid ${({ isActive, isCompleted, theme }) => {
    if (isCompleted) return theme.colors.status.online;
    if (isActive) return 'transparent';
    return theme.colors.dark.border;
  }};
  transition: all ${({ theme }) => theme.transitions.normal};
  box-shadow: ${({ isActive, theme }) => 
    isActive ? `0 0 20px ${theme.colors.primary[500]}40` : 'none'
  };
  
  svg {
    width: 22px;
    height: 22px;
    color: ${({ isActive, isCompleted, theme }) => {
      if (isCompleted || isActive) return 'white';
      return theme.colors.dark.textSecondary;
    }};
  }
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const StepConnector = styled.div`
  width: 180px;
  height: 3px;
  background: ${({ isCompleted, theme }) => 
    isCompleted ? theme.colors.status.online : theme.colors.dark.border
  };
  transition: all ${({ theme }) => theme.transitions.normal};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  
  @media (max-width: 768px) {
    width: 60px;
    height: 2px;
  }
`;

const StepLabel = styled.div`
  position: absolute;
  top: 58px;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ isActive, theme }) => 
    isActive ? theme.colors.primary[400] : theme.colors.dark.textSecondary
  };
  white-space: nowrap;
  transform: translateX(-50%);
  left: 50%;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  @media (max-width: 768px) {
    top: 50px;
    font-size: 0.75rem;
  }
`;

const EnrollmentSteps = ({ currentStep }) => {
  const steps = [
    { id: 'intro', label: 'Welcome', icon: Shield },
    { id: 'recording', label: 'Train Voice', icon: Mic },
    { id: 'verification', label: 'Verify', icon: CheckCircle }
  ];

  const getStepStatus = (stepId) => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    const stepIndex = steps.findIndex(s => s.id === stepId);
    
    return {
      isActive: stepId === currentStep,
      isCompleted: stepIndex < currentIndex || currentStep === 'complete'
    };
  };

  return (
    <StepsContainer>
      {steps.map((step, index) => {
        const { isActive, isCompleted } = getStepStatus(step.id);
        const Icon = step.icon;
        
        return (
          <React.Fragment key={step.id}>
            <StepItem style={{ position: 'relative' }}>
              <StepCircle
                isActive={isActive}
                isCompleted={isCompleted}
                animate={isActive ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon />
              </StepCircle>
              <StepLabel isActive={isActive}>{step.label}</StepLabel>
            </StepItem>
            {index < steps.length - 1 && (
              <StepConnector isCompleted={isCompleted} />
            )}
          </React.Fragment>
        );
      })}
    </StepsContainer>
  );
};

export default EnrollmentSteps;
